import React from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
  Button, FormControl, FormLabel, Input, Select, Flex, Text, Alert, AlertIcon,
  Switch, Textarea, Divider,
} from '@chakra-ui/react';
import * as FiIcons from 'react-icons/fi';
import { updateServer } from '../../../../../store/servers/serversSlice';
import theme from '../../../../global/theme';

const UpdateModal = ({ data, onClose }) => {
  const dispatch = useDispatch();
  const servers = useSelector(state => state.servers);
  const { register, handleSubmit, formState: { errors } } = useForm({ defaultValues: { order: 0, ...data } });

  const onSubmit = (values) => {
    const fd = new FormData();
    fd.append('_id', data._id);
    fd.append('prev_image', data.image || '');
    Object.entries(values).forEach(([k, v]) => {
      if (k === 'image' && v?.[0]) fd.append('image', v[0]);
      else if (v !== undefined && v !== null && k !== '_id') fd.append(k, v);
    });
    dispatch(updateServer(fd)).unwrap().then(() => onClose());
  };

  const inputProps = { bg: theme.light, border: 'none', borderRadius: 4 };

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent borderRadius={4} py={4} bg={theme.dark}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader textAlign="center" color="orange" textTransform="uppercase" fontSize={20}>
            تعديل السيرفر
          </ModalHeader>

          <ModalBody>
            {servers.errors.length > 0 && (
              <Alert status="error" variant="left-accent" marginBottom={4}>
                <AlertIcon />
                {servers.errors.map((e, i) => <Text key={i}>{e?.msg}</Text>)}
              </Alert>
            )}

            <Flex gap={3} mb={4} flexWrap="wrap">
              <FormControl flex="1" minW="180px">
                <FormLabel color={theme.light} fontSize={13}>الاسم بالعربي</FormLabel>
                <Input {...inputProps} placeholder="اسم السيرفر بالعربي" {...register('name_ar')} />
              </FormControl>
              <FormControl flex="1" minW="180px">
                <FormLabel color={theme.light} fontSize={13}>الاسم بالإنجليزي</FormLabel>
                <Input {...inputProps} placeholder="Server name in English" {...register('name_en')} />
              </FormControl>
            </Flex>

            <Divider borderColor="whiteAlpha.100" mb={4} />
            <Flex gap={3} mb={4} flexWrap="wrap">
              <FormControl flex="1" minW="180px">
                <FormLabel color={theme.light} fontSize={13}>الوصف بالعربي</FormLabel>
                <Textarea {...inputProps} rows={2} resize="none" placeholder="وصف السيرفر بالعربي" {...register('description_ar')} />
              </FormControl>
              <FormControl flex="1" minW="180px">
                <FormLabel color={theme.light} fontSize={13}>الوصف بالإنجليزي</FormLabel>
                <Textarea {...inputProps} rows={2} resize="none" placeholder="Server description in English" {...register('description_en')} />
              </FormControl>
            </Flex>

            <Divider borderColor="whiteAlpha.100" mb={4} />

            {/* ── TYPE & LEVEL ── */}
            <Flex gap={4} mb={4}>
              <FormControl>
                <FormLabel color={theme.light} fontSize={13}>النوع</FormLabel>
                <Select {...inputProps} {...register('type')}>
                  <option value="newschool">New School</option>
                  <option value="oldschool">Old School</option>
                  <option value="middleschool">Middle School</option>
                  <option value="custom">Custom</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel color={theme.light} fontSize={13}>لغة السيرفر</FormLabel>
                <Select {...inputProps} {...register('server_language')}>
                  <option value="arabic">عربي</option>
                  <option value="foreign">أجنبي</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel color={theme.light} fontSize={13}>أقصى مستوى</FormLabel>
                <Input type="number" {...inputProps} {...register('level')} />
              </FormControl>
            </Flex>

            <Flex gap={4} mb={4}>
              <FormControl>
                <FormLabel color={theme.light} fontSize={13}>نسبة الحروب %</FormLabel>
                <Input type="number" {...inputProps} {...register('wars')} />
              </FormControl>
              <FormControl>
                <FormLabel color={theme.light} fontSize={13}>نسبة الزراعة %</FormLabel>
                <Input type="number" {...inputProps} {...register('farms')} />
              </FormControl>
              <FormControl>
                <FormLabel color={theme.light} fontSize={13}>ترتيب الظهور بالرئيسية</FormLabel>
                <Input type="number" {...inputProps} {...register('order')} />
              </FormControl>
              <FormControl>
                <FormLabel color={theme.light} fontSize={13}>صاحب السيرفر</FormLabel>
                <Select {...inputProps} {...register('fame')}>
                  <option value="unknown">غير معروف</option>
                  <option value="known">معروف</option>
                  <option value="famous">مشهور</option>
                </Select>
              </FormControl>
            </Flex>

            <FormControl mb={4}>
              <FormLabel color={theme.light} fontSize={13}>رابط الموقع</FormLabel>
              <Input {...inputProps} {...register('website_url')} />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel color={theme.light} fontSize={13}>رابط ديسكورد</FormLabel>
              <Input {...inputProps} {...register('discord_url')} />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel color={theme.light} fontSize={13}>صورة جديدة (اختياري)</FormLabel>
              <Input type="file" accept="image/*" {...inputProps} p={1} {...register('image')} />
            </FormControl>

            <Divider borderColor="whiteAlpha.100" mb={4} />

            <Flex gap={6} flexWrap="wrap">
              <FormControl display="flex" alignItems="center" width="auto">
                <FormLabel color={theme.light} mb={0} fontSize={13}>نشط</FormLabel>
                <Switch colorScheme="green" {...register('is_active')} defaultChecked={data.is_active} />
              </FormControl>
              <FormControl display="flex" alignItems="center" width="auto">
                <FormLabel color="yellow.300" mb={0} fontSize={13}>عرض في الهيدر</FormLabel>
                <Switch colorScheme="yellow" {...register('showInHeader')} defaultChecked={data.showInHeader} />
              </FormControl>
              <FormControl display="flex" alignItems="center" width="auto">
                <FormLabel color="cyan.300" mb={0} fontSize={13}>عرض في الرئيسية</FormLabel>
                <Switch colorScheme="cyan" {...register('showInHome')} defaultChecked={data.showInHome} />
              </FormControl>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Flex justifyContent="flex-end" gap={3}>
              <Button type="submit" rightIcon={<FiIcons.FiSave />} colorScheme="green"
                isLoading={servers.isLoading}>
                حفظ
              </Button>
              <Button type="button" rightIcon={<FiIcons.FiX />} colorScheme="red"
                isLoading={servers.isLoading} onClick={onClose}>
                إلغاء
              </Button>
            </Flex>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default UpdateModal;
